import React, { useState, useCallback, useEffect } from "react"
import API from "../../utils/API"
import Sweetalert from "../../utils/Sweetalert"
import MonitorForm from "./MonitorForm"

const PHASES = {
  LOADING: "LOADING",
  TASK_LIST: "TASK_LIST",
  CREATE_TASK: "CREATE_TASK",
  EDIT_TASK: "EDIT_TASK",
  MONITOR_FORM: "MONITOR_FORM",
}

const EMPTY_TASK_FORM = {
  task_name: "",
  enable: true,
  schedule_plan_id: "1",
}

export default function TaskManagerModal({ totemId, onClose }) {
  const [phase, setPhase] = useState(PHASES.LOADING)
  const [tasks, setTasks] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM)
  const [editingTask, setEditingTask] = useState(null)
  const [monitorContext, setMonitorContext] = useState(null)
  const [monitorData, setMonitorData] = useState(null)
  const [allMonitors, setAllMonitors] = useState([])
  const [monitorLoading, setMonitorLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)

  const fetchTasks = useCallback(() => {
    setLoading(true)
    setError(null)
    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/tasks`,
      { offset: 0, size: 100 },
      (response) => {
        const data = response.data?.data || response.data || {}
        setTasks(data.list || [])
        setLoading(false)
        setPhase(PHASES.TASK_LIST)
      },
      (errorMessage) => {
        setError(errorMessage || "Impossible de charger les tâches.")
        setLoading(false)
        setPhase(PHASES.TASK_LIST)
      },
    )
  }, [totemId])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const handleCreateTask = useCallback(
    (e) => {
      e.preventDefault()
      setActionBusy(true)
      setError(null)

      const payload = {
        task_type: "single_point_task",
        task_name: taskForm.task_name.trim(),
        enable: taskForm.enable,
        schedule_plan_id: taskForm.schedule_plan_id,
        device_list: [{ device_id: 1 }],
        algo_card_type: ["face"],
      }

      API.CUSTOM(
        "POST",
        `/admin/totem/${totemId}/ai-targeting-module/tasks/create`,
        payload,
        () => {
          setActionBusy(false)
          setTaskForm(EMPTY_TASK_FORM)
          fetchTasks()
        },
        (err) => {
          setActionBusy(false)
          setError(
            err?.response?.data?.error || err?.message || "Erreur lors de la création.",
          )
        },
      )
    },
    [taskForm, totemId, fetchTasks],
  )

  const handleUpdateTask = useCallback(
    (e) => {
      e.preventDefault()
      if (!editingTask) return
      setActionBusy(true)
      setError(null)

      const payload = {
        task_id: editingTask.task_id,
        task_type: "single_point_task",
        task_name: taskForm.task_name.trim(),
        enable: taskForm.enable,
        schedule_plan_id: taskForm.schedule_plan_id,
        device_list: editingTask.device_list || [],
        algo_card_type: editingTask.algo_card_type || "face",
      }

      API.CUSTOM(
        "PUT",
        `/admin/totem/${totemId}/ai-targeting-module/tasks`,
        payload,
        () => {
          setActionBusy(false)
          setEditingTask(null)
          setTaskForm(EMPTY_TASK_FORM)
          fetchTasks()
        },
        (err) => {
          setActionBusy(false)
          setError(
            err?.response?.data?.error || err?.message || "Erreur lors de la mise à jour.",
          )
        },
      )
    },
    [taskForm, editingTask, totemId, fetchTasks],
  )

  const handleDeleteTask = useCallback(
    (task) => {
      Sweetalert.fireDeleteConfirm(
        "Supprimer la tâche",
        `La tâche "${task.task_name}" sera définitivement supprimée.`,
        () => {
          setActionBusy(true)
          setError(null)

          API.CUSTOM(
            "DELETE",
            `/admin/totem/${totemId}/ai-targeting-module/tasks`,
            { task_id: task.task_id },
            () => {
              setActionBusy(false)
              fetchTasks()
            },
            (err) => {
              setActionBusy(false)
              setError(
                err?.response?.data?.error || err?.message || "Erreur lors de la suppression.",
              )
            },
          )
        },
      )
    },
    [totemId, fetchTasks],
  )

  const handleToggleEnable = useCallback(
    (task) => {
      setError(null)
      const payload = {
        task_id: task.task_id,
        task_type: task.task_type || "single_point_task",
        task_name: task.task_name,
        enable: !task.enable,
        schedule_plan_id: task.schedule_plan_id,
        device_list: task.device_list || [],
        algo_card_type: task.algo_card_type || "face",
      }

      API.CUSTOM(
        "PUT",
        `/admin/totem/${totemId}/ai-targeting-module/tasks`,
        payload,
        () => fetchTasks(),
        (err) => {
          setError(
            err?.response?.data?.error || err?.message || "Erreur lors de la mise à jour.",
          )
        },
      )
    },
    [totemId, fetchTasks],
  )

  const openEditTask = useCallback((task) => {
    setEditingTask(task)
    setTaskForm({
      task_name: task.task_name || "",
      enable: !!task.enable,
      schedule_plan_id: task.schedule_plan_id ?? "1",
    })
    setError(null)
    setPhase(PHASES.EDIT_TASK)
  }, [])

  const openMonitor = useCallback(
    (task) => {
      setMonitorContext(task)
      setMonitorLoading(true)
      setMonitorData(null)
      setError(null)

      API.POST(
        `/admin/totem/${totemId}/ai-targeting-module/monitors`,
        { offset: 0, size: 100 },
        (response) => {
          const data = response.data?.data || response.data || {}
          const list = data.list || data.param || []
          const taskDeviceIds = (task.device_list || []).map((d) => d.device_id)
          const existing = list.find(
            (m) => taskDeviceIds.includes(m.common_param?.device_id),
          ) || null
          setAllMonitors(list)
          setMonitorData(existing)
          setMonitorLoading(false)
          setPhase(PHASES.MONITOR_FORM)
        },
        (errorMessage) => {
          setError(errorMessage || "Impossible de charger les moniteurs.")
          setMonitorLoading(false)
        },
      )
    },
    [totemId],
  )

  const goToList = useCallback(() => {
    setPhase(PHASES.TASK_LIST)
    setEditingTask(null)
    setTaskForm(EMPTY_TASK_FORM)
    setMonitorContext(null)
    setMonitorData(null)
    setAllMonitors([])
    setError(null)
  }, [])

  const busy = loading || actionBusy

  return (
    <>
      <div className="modal fade show" style={{ display: "block", overflowY: "auto" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-tasks mr-2"></i>
                {phase === PHASES.MONITOR_FORM
                  ? `Moniteur — ${monitorContext?.task_name || "Tâche"}`
                  : phase === PHASES.CREATE_TASK
                    ? "Créer une tâche"
                    : phase === PHASES.EDIT_TASK
                      ? "Modifier la tâche"
                      : "Gestion des tâches IA"}
              </h5>
              <button type="button" className="close" onClick={onClose} disabled={busy}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body" style={{ minHeight: 300, maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
              {error && (
                <div className="alert alert-danger py-2">
                  <i className="fas fa-exclamation-triangle mr-1"></i> {error}
                </div>
              )}

              {(phase === PHASES.LOADING || loading) && (
                <div className="text-center py-4">
                  <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2 text-muted">Chargement des tâches...</p>
                </div>
              )}

              {phase === PHASES.TASK_LIST && !loading && renderTaskList()}
              {phase === PHASES.CREATE_TASK && renderTaskForm(false)}
              {phase === PHASES.EDIT_TASK && renderTaskForm(true)}
              {phase === PHASES.MONITOR_FORM && renderMonitorPhase()}
            </div>

            <div className="modal-footer">
              {phase === PHASES.TASK_LIST && (
                <button type="button" className="btn btn-default" onClick={onClose}>
                  Fermer
                </button>
              )}
              {(phase === PHASES.CREATE_TASK || phase === PHASES.EDIT_TASK || phase === PHASES.MONITOR_FORM) && (
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={goToList}
                  disabled={busy}
                >
                  <i className="fas fa-arrow-left mr-1"></i> Retour à la liste
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )

  function renderTaskList() {
    return (
      <>
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <span className="text-muted">
            {tasks.length} tâche{tasks.length !== 1 ? "s" : ""} trouvée{tasks.length !== 1 ? "s" : ""}
          </span>
          <div>
            <button
              className="btn btn-sm btn-info mr-2"
              onClick={fetchTasks}
              disabled={busy}
            >
              <i className="fas fa-sync-alt mr-1"></i> Rafraîchir
            </button>
            <button
              className="btn btn-sm btn-success"
              onClick={() => {
                setTaskForm(EMPTY_TASK_FORM)
                setError(null)
                setPhase(PHASES.CREATE_TASK)
              }}
              disabled={busy}
            >
              <i className="fas fa-plus mr-1"></i> Créer une tâche
            </button>
          </div>
        </div>

        {tasks.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-inbox fa-2x mb-2"></i>
            <p>Aucune tâche configurée sur ce module.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Nom</th>
                  <th style={{ width: 120 }}>Type</th>
                  <th style={{ width: 90 }}>Activée</th>
                  <th style={{ width: 140 }}>Périphériques</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.task_id}>
                    <td>{task.task_name || "-"}</td>
                    <td>
                      <span className="badge badge-info">
                        {task.task_type === "single_point_task" ? "Point unique" : task.task_type || "-"}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="custom-control custom-switch d-inline-block">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`task-enable-${task.task_id}`}
                          checked={!!task.enable}
                          onChange={() => handleToggleEnable(task)}
                          disabled={busy}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor={`task-enable-${task.task_id}`}
                        ></label>
                      </div>
                    </td>
                    <td>
                      {task.device_list?.length > 0
                        ? `${task.device_list.length} périph.`
                        : <span className="text-muted">Aucun</span>}
                    </td>
                    <td>
                      <button
                        className="btn btn-xs btn-outline-primary mr-1"
                        onClick={() => openMonitor(task)}
                        disabled={busy}
                        title="Gérer le moniteur"
                      >
                        <i className="fas fa-eye mr-1"></i> Moniteur
                      </button>
                      <button
                        className="btn btn-xs btn-outline-warning mr-1"
                        onClick={() => openEditTask(task)}
                        disabled={busy}
                        title="Modifier la tâche"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-xs btn-outline-danger"
                        onClick={() => handleDeleteTask(task)}
                        disabled={busy}
                        title="Supprimer la tâche"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  function renderTaskForm(isEdit) {
    const onSubmit = isEdit ? handleUpdateTask : handleCreateTask

    return (
      <form onSubmit={onSubmit}>
        <div className="card card-primary card-outline">
          <div className="card-header py-2">
            <h6 className="card-title mb-0">
              <i className="fas fa-cog mr-1"></i>
              {isEdit ? "Modifier la tâche" : "Nouvelle tâche"}
            </h6>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label htmlFor="task-name">
                Nom de la tâche <span className="text-danger">*</span>
              </label>
              <input
                id="task-name"
                type="text"
                className="form-control"
                value={taskForm.task_name}
                onChange={(e) =>
                  setTaskForm((prev) => ({ ...prev, task_name: e.target.value }))
                }
                required
                maxLength={64}
                disabled={actionBusy}
              />
            </div>

            <div className="form-group">
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="task-enable"
                  checked={taskForm.enable}
                  onChange={(e) =>
                    setTaskForm((prev) => ({ ...prev, enable: e.target.checked }))
                  }
                  disabled={actionBusy}
                />
                <label className="custom-control-label" htmlFor="task-enable">
                  Tâche activée
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="task-schedule">
                ID du plan de programmation{" "}
                <small className="text-muted">(1 = toujours actif)</small>
              </label>
              <input
                id="task-schedule"
                type="text"
                className="form-control"
                value={taskForm.schedule_plan_id}
                onChange={(e) =>
                  setTaskForm((prev) => ({ ...prev, schedule_plan_id: e.target.value }))
                }
                placeholder="1"
                disabled={actionBusy}
              />
            </div>

            <div className="form-group">
              <label>Type de tâche</label>
              <input
                type="text"
                className="form-control"
                value="Point unique (single_point_task)"
                disabled
              />
              <small className="text-muted">
                Seul le type "single_point_task" est supporté actuellement.
              </small>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-default mr-2"
            onClick={goToList}
            disabled={actionBusy}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={actionBusy || !taskForm.task_name.trim()}
          >
            {actionBusy && (
              <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
            )}
            <i className="fas fa-save mr-1"></i>
            {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </form>
    )
  }

  function renderMonitorPhase() {
    if (monitorLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-info" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
          <p className="mt-2 text-muted">Chargement du moniteur...</p>
        </div>
      )
    }

    return (
      <MonitorForm
        totemId={totemId}
        taskId={monitorContext?.task_id}
        monitor={monitorData}
        allMonitors={allMonitors}
        onSave={() => {
          goToList()
          fetchTasks()
        }}
        onCancel={goToList}
      />
    )
  }
}
